import type { AuthSessionDto, AuthUserDto, OtpPurpose, Role } from '@talentbabu/types';
import type {
  ChangePasswordInput,
  ForgotPasswordInput,
  ForgotPasswordOtpInput,
  ForgotPasswordVerifyInput,
  LoginAudience,
  LoginInput,
  RegisterCandidateInput,
  RegisterEmployerInput,
  ResetPasswordInput,
  UpdateContactInput,
} from '@talentbabu/validation';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../app/auth-store';
import { apiGet, apiPatch, apiPost } from '../../lib/api';
import { errorMessage } from '../../lib/errors';

/** Roles each dedicated login page accepts; mirrors the API, which is the real enforcement. */
const AUDIENCE_RULES: Record<LoginAudience, { roles: readonly Role[]; notFound: string }> = {
  candidate: { roles: ['CANDIDATE'], notFound: 'Candidate not found' },
  employer: { roles: ['EMPLOYER', 'HR_MANAGER'], notFound: 'Employer not found' },
  admin: { roles: ['SUPER_ADMIN'], notFound: 'Super Admin not found' },
};

export class WrongLoginPageError extends Error {}

/** Error text for login forms: the wrong-page message, or the usual API error message. */
export const loginErrorMessage = (err: unknown) =>
  err instanceof WrongLoginPageError ? err.message : errorMessage(err);

/**
 * Second line of defence behind the API check: a session for the wrong role is revoked
 * before it is stored, so the user is never signed in through the wrong login page.
 */
async function assertAudience(session: AuthSessionDto, audience: LoginAudience | undefined) {
  if (!audience) return;
  const rule = AUDIENCE_RULES[audience];
  if (rule.roles.includes(session.user.role)) return;
  await apiPost<null>('/auth/logout', {}).catch(() => undefined);
  throw new WrongLoginPageError(rule.notFound);
}

function useSessionMutation<TInput>(url: string) {
  const setSession = useAuthStore((s) => s.setSession);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: TInput) => {
      const session = (await apiPost<AuthSessionDto>(url, input)).data;
      await assertAudience(session, (input as { audience?: LoginAudience }).audience);
      return session;
    },
    onSuccess: (session) => {
      queryClient.clear();
      setSession(session);
    },
  });
}

export const useLogin = () => useSessionMutation<LoginInput>('/auth/login');
export const useRegisterCandidate = () => useSessionMutation<RegisterCandidateInput>('/auth/register/candidate');
export const useRegisterEmployer = () => useSessionMutation<RegisterEmployerInput>('/auth/register/employer');

export interface OtpSendResult {
  expiresInSec: number;
  resendInSec: number;
}

export function useSendOtp() {
  return useMutation({
    mutationFn: async (input: { purpose: OtpPurpose; mobile?: string }) =>
      (await apiPost<OtpSendResult>('/auth/otp/send', input)).data,
  });
}

/** LOGIN returns a session, VERIFY_MOBILE confirms, RESET_PASSWORD returns a reset token. */
export function useVerifyOtp() {
  const setSession = useAuthStore((s) => s.setSession);
  const refreshUser = useRefreshUser();
  return useMutation({
    mutationFn: async (input: { purpose: OtpPurpose; mobile?: string; code: string; audience?: LoginAudience }) => {
      const data = (
        await apiPost<AuthSessionDto | { mobileVerified: true } | { resetToken: string }>('/auth/otp/verify', input)
      ).data;
      if ('accessToken' in data) await assertAudience(data, input.audience);
      return data;
    },
    onSuccess: async (data) => {
      if ('accessToken' in data) setSession(data);
      if ('mobileVerified' in data) await refreshUser();
    },
  });
}

export function useResendEmailVerification() {
  return useMutation({ mutationFn: async () => apiPost<null>('/auth/email/verify/send') });
}

export function useVerifyEmail() {
  const refreshUser = useRefreshUser();
  return useMutation({
    mutationFn: async (token: string) => apiPost<{ emailVerified: true }>('/auth/email/verify', { token }),
    onSuccess: async () => {
      if (useAuthStore.getState().accessToken) await refreshUser();
    },
  });
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: async (input: ForgotPasswordInput) => apiPost<null>('/auth/password/forgot', input),
  });
}

/** Forgot Password: sends the OTP to the registered email or mobile number that was entered. */
export function useSendForgotPasswordOtp() {
  return useMutation({
    mutationFn: async (input: ForgotPasswordOtpInput) =>
      (await apiPost<OtpSendResult>('/auth/password/forgot/otp', input)).data,
  });
}

/** Checks the OTP and returns the one-time token that `useResetPassword` needs. */
export function useVerifyForgotPasswordOtp() {
  return useMutation({
    mutationFn: async (input: ForgotPasswordVerifyInput) =>
      (await apiPost<{ resetToken: string }>('/auth/password/forgot/verify', input)).data,
  });
}

export function useResetPassword() {
  return useMutation({ mutationFn: async (input: ResetPasswordInput) => apiPost<null>('/auth/password/reset', input) });
}

export function useChangePassword() {
  const setSession = useAuthStore((s) => s.setSession);
  return useMutation({
    mutationFn: async (input: ChangePasswordInput) =>
      (await apiPost<AuthSessionDto>('/auth/password/change', input)).data,
    onSuccess: setSession,
  });
}

/** Emails the signed-in user the one-time code required to change the password. */
export function useSendPasswordChangeOtp() {
  return useMutation({ mutationFn: async () => apiPost<null>('/auth/password/change/otp', {}) });
}

/** Edits the signed-in user's unverified email / mobile; the API refuses verified ones. */
export function useChangeContact() {
  const setUser = useAuthStore((s) => s.setUser);
  return useMutation({
    mutationFn: async (input: UpdateContactInput) => (await apiPatch<AuthUserDto>('/auth/me/contact', input)).data,
    onSuccess: setUser,
  });
}

/** Uploads the employer's profile photo; the sidebar reads it from the signed-in user. */
export function useUploadEmployerPhoto() {
  const setUser = useAuthStore((s) => s.setUser);
  return useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return (await apiPost<AuthUserDto>('/auth/me/photo', form)).data;
    },
    onSuccess: setUser,
  });
}

export function useLogout() {
  const clear = useAuthStore((s) => s.clear);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (everywhere: boolean = false) =>
      apiPost<null>(everywhere ? '/auth/logout-all' : '/auth/logout', {}),
    onSettled: () => {
      clear();
      queryClient.clear();
    },
  });
}

/** Re-fetches /auth/me (after verification or company status changes). */
export function useRefreshUser() {
  const setUser = useAuthStore((s) => s.setUser);
  return async () => {
    const me = await apiGet<AuthUserDto>('/auth/me');
    setUser(me.data);
    return me.data;
  };
}
